// src/components/properties/property-view/property-detail-drawer/PropertyDetailDrawer.tsx

import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography';
import { useGetPhotosetsQuery } from '@/components/property-assets-management/services/assetApi';
import { ROUTES, buildRoute } from '@/config/routes';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import type { PropertyEntity } from '@/types/property.types';
import { DrawerActionRow } from './DrawerActionRow';
import {
  HeroCarousel,
  RecentOrdersSection,
  pickLatestPhotoset,
} from '@/components/properties/property-shared';
import { PropertyDetailsSection } from './PropertyDetailsSection';
import {
  type HeroSource,
  type PropertyDetailDrawerProps,
} from './types';

export const PropertyDetailDrawer = ({
  property,
  onOpenChange,
  onAddToCart,
  isAddingToCart = false,
  isInCart = false,
}: PropertyDetailDrawerProps) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const open = !!property;

  // Photoset fetch — shared assetApi slice. Skip when closed.
  const photosetsQuery = useGetPhotosetsQuery(property?.id ?? '', {
    skip: !open || !property?.id,
  });

  const heroSource: HeroSource = {
    logoUrl: ((property?.apiResponseRaw ?? {}) as PropertyEntity).logoUrl ?? null,
    photoset: pickLatestPhotoset(photosetsQuery.currentData) ?? null,
    isPhotosetLoading: photosetsQuery.isFetching,
  };

  const entity = (property?.apiResponseRaw ?? {}) as PropertyEntity;
  const formattedAddress = property?.formattedAddress ?? '';
  const [addressLine1, ...addressRest] = formattedAddress.split('\n').filter(Boolean);
  const subtitle =
    (property?.portfolioName as string | null) || (entity.entity ?? null);

  const handleAddToCart = () => {
    if (!property) return;
    onAddToCart(property);
    // Drawer stays open — toast confirms (per spec §6).
  };

  const handleManageAssets = () => {
    if (!property) return;
    navigate(buildRoute.propertyAssets(property.id));
    onOpenChange(false);
  };

  const handleEdit = () => {
    if (!property) return;
    navigate(buildRoute.propertyDetail(property.id));
    onOpenChange(false);
  };

  const handleViewAllOrders = () => {
    if (!property) return;
    const param = encodeURIComponent(formattedAddress.split('\n')[0] ?? '');
    navigate(`${ROUTES.ORDERS}?propertyAddress=${param}`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={cn(
          'flex flex-col gap-0 p-0 [&>button]:hidden',
          isMobile
            ? 'h-[92vh] w-full rounded-t-xl'
            : 'w-full sm:max-w-[480px]',
        )}
      >
        {property && (
          <>
            <SheetTitle className="sr-only">
              Property details for {addressLine1}
            </SheetTitle>
            <div className="relative shrink-0">
              <HeroCarousel
                key={property.id}
                source={heroSource}
                leasingStatus={entity.propertyStatus ?? null}
                ariaLabel={addressLine1 ?? 'Property image'}
              />
              <SheetClose asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
                >
                  <X className="h-4 w-4" />
                </button>
              </SheetClose>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              <div className="space-y-1">
                <Typography as="p" variant="heading-lg">
                  {addressLine1}
                </Typography>
                {addressRest.length > 0 && (
                  <Typography as="p" variant="small" className="text-muted-foreground">
                    {addressRest.join(' · ')}
                    {subtitle ? ` · ${subtitle}` : ''}
                  </Typography>
                )}
              </div>

              <DrawerActionRow
                onAddToCart={handleAddToCart}
                onManageAssets={handleManageAssets}
                onOpen={handleEdit}
                isAddingToCart={isAddingToCart}
                isInCart={isInCart}
                hasPhotoset={property.hasPhotoset === true}
              />

              <PropertyDetailsSection property={property} />

              <RecentOrdersSection
                propertyId={property.id}
                open={open}
                onViewAll={handleViewAllOrders}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
